import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Alert, Button, Checkbox, Col, Form, Input, Row, Switch, Typography } from "antd";
import { runInAction } from "mobx";
import { observer } from "mobx-react";
import { useCallback, useEffect, useState } from "react";
import { ECheckoutCustomerType } from "@devowl-wp/api-real-commerce";
import { AddressFields, WANTS_INVOICE_ADDRESS_FIELD } from "./AddressFields.js";
import { CUSTOMER_TYPE_FIELD, CustomerTypeSwitch } from "./CustomerTypeSwitch.js";
import { TeachingsSection } from "./TeachingsSection.js";
import { VatIdField } from "./VatIdField.js";
import { RealCommerceError } from "../../../../exceptions/realCommerceError.js";
import { useRealCommerceErrorHandling } from "../../../../hooks/useRealCommerceErrorHandling.js";
import { useStores } from "../../../../store/stores.js";
import { __ } from "../../../../utils/i18n.js";
import { MATOMO_MASK } from "../../../../utils/matomoMask.js";
import { useRecoverableFormV2 } from "../../hooks/useRecoverableFormV2.js";
import { COUPON_FORM_VALUE_KEY } from "../right/CouponInput.js";
function hasAnyTextInput(changed) {
    return Object.values(changed).some((value)=>typeof value === "string" && value.trim().length > 0);
}
/**
 * "Your details" form. On submit creates the Paddle incomplete checkout
 * via `transaction.applyIdentification()`. Business buyers always include
 * the invoice address; individuals opt in via `wantsInvoiceAddress`.
 */ const IdentificationForm = observer(()=>{
    const { checkoutV2Store } = useStores();
    const overwriteCheckoutCountry = checkoutV2Store.product?.data.paddlePayLink?.overwriteCheckoutCountry;
    const { form, initialValues, makeRestorable } = useRecoverableFormV2({
        overwriteCheckoutCountry
    });
    const [isBusy, setIsBusy] = useState(false);
    // `isBusy` only flips after Antd's field validators resolve (VAT ID is a
    // server round-trip, >1 s). Track the click separately so the spinner
    // is immediate; `onFinishFailed` resets it if validation rejects.
    const [submitRequested, setSubmitRequested] = useState(false);
    // Inline `CheckoutEmailMxDoNotExist` override: surface Alert + confirm
    // checkbox under the email field instead of the global modal; checking
    // the box + re-submitting sends `disableEmailCheck: true`. The error
    // remembers the email it was raised for so editing to a different value
    // clears it automatically (assumed-fixed typo).
    const [emailMxError, setEmailMxError] = useState(undefined);
    const [disableEmailCheck, setDisableEmailCheck] = useState(false);
    const handleRealCommerceError = useRealCommerceErrorHandling();
    const customerType = Form.useWatch(CUSTOMER_TYPE_FIELD, form);
    const emailValue = Form.useWatch("email", form);
    // Clear the MX warning once the email changes — the old value was the only thing it applied to.
    useEffect(()=>{
        if (emailMxError && emailValue !== emailMxError.email) {
            setEmailMxError(undefined);
            setDisableEmailCheck(false);
        }
    }, [
        emailValue,
        emailMxError
    ]);
    const handleSubmit = useCallback(async (values)=>{
        setIsBusy(true);
        try {
            const seededCoupon = checkoutV2Store.formValues[COUPON_FORM_VALUE_KEY];
            const isInvoiceAddressGiven = values.customerType === "company" || !!values.wantsInvoiceAddress;
            await checkoutV2Store.transaction.applyIdentification({
                customerType: values.customerType === "company" ? ECheckoutCustomerType.Company : ECheckoutCustomerType.Individual,
                email: values.email,
                firstName: values.firstName,
                lastName: values.lastName,
                zip: values.zip,
                country: values.country,
                ...isInvoiceAddressGiven ? {
                    companyName: values.companyName,
                    street: values.street,
                    city: values.city,
                    vatId: values.vatId || undefined
                } : {},
                coupon: seededCoupon || undefined
            }, {
                // B2B renders no terms checkbox (disclaimer links stay visible below the form),
                // but the backend still requires the flag — send implicit `true`.
                isTermsAccepted: values.customerType === "company" ? true : values.isTermsAccepted,
                isNewsletterSignUpAccepted: Boolean(values.isNewsletterSignUpAccepted)
            }, isInvoiceAddressGiven, disableEmailCheck);
            // Successful submit — clear any previous MX-check override.
            setEmailMxError(undefined);
            setDisableEmailCheck(false);
            makeRestorable(values, seededCoupon);
        } catch (e) {
            if (e instanceof RealCommerceError) {
                if (e.hasErrorCode("CheckoutEmailMxDoNotExist")) {
                    // Inline override UX instead of the global error modal.
                    setEmailMxError({
                        email: values.email,
                        message: e.message
                    });
                } else if (e.hasErrorCode("CheckoutCouponNotFoundOrExpired")) {
                    // Drop the invalid coupon so a retry (or a direct
                    // Continue-to-payment click) proceeds without it,
                    // and replace localStorage cache accordingly. The
                    // right-column input observes the notice and resets
                    // itself.
                    runInAction(()=>{
                        delete checkoutV2Store.formValues[COUPON_FORM_VALUE_KEY];
                    });
                    makeRestorable(values, undefined);
                    checkoutV2Store.setInvalidCouponNotice(__("Coupon invalid and removed. Try another or continue without!"));
                } else {
                    handleRealCommerceError(e, {
                        onBackendDown: (error)=>checkoutV2Store.setFatalError(error)
                    });
                }
            }
        } finally{
            setIsBusy(false);
            setSubmitRequested(false);
        }
    }, [
        checkoutV2Store,
        handleRealCommerceError,
        makeRestorable,
        disableEmailCheck
    ]);
    // Reset the immediate-spinner flag when a validator rejects (e.g. invalid VAT ID).
    const handleSubmitFailed = useCallback(()=>{
        setSubmitRequested(false);
    }, []);
    const handleValuesChange = useCallback((changed, all)=>{
        if (hasAnyTextInput(changed)) {
            checkoutV2Store.markCheckoutFormInteraction();
        }
        // Persist every change (including non-text toggles) so a reload restores the latest input.
        const seededCoupon = checkoutV2Store.formValues[COUPON_FORM_VALUE_KEY];
        makeRestorable(all, seededCoupon);
    }, [
        checkoutV2Store,
        makeRestorable
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Typography.Title, {
                level: 5,
                className: "checkout-v2-section-heading",
                style: {
                    marginTop: 0,
                    marginBottom: 12
                },
                children: __("Your Details")
            }),
            /*#__PURE__*/ _jsxs(Form, {
                form: form,
                layout: "vertical",
                variant: "outlined",
                initialValues: initialValues,
                onFinish: handleSubmit,
                onFinishFailed: handleSubmitFailed,
                onValuesChange: handleValuesChange,
                disabled: isBusy,
                children: [
                    /*#__PURE__*/ _jsx(CustomerTypeSwitch, {}),
                    /*#__PURE__*/ _jsx(Form.Item, {
                        name: "email",
                        label: __("Email address"),
                        required: true,
                        rules: [
                            {
                                type: "email",
                                required: true,
                                message: __("Please enter a valid email address.")
                            }
                        ],
                        children: /*#__PURE__*/ _jsx(Input, {
                            type: "email",
                            placeholder: __("example@company.com"),
                            autoComplete: "email",
                            ...MATOMO_MASK
                        })
                    }),
                    emailMxError && /*#__PURE__*/ _jsxs("div", {
                        style: {
                            marginTop: -12,
                            marginBottom: 16
                        },
                        children: [
                            /*#__PURE__*/ _jsx(Alert, {
                                type: "warning",
                                showIcon: true,
                                message: emailMxError.message,
                                style: {
                                    padding: "6px 12px"
                                }
                            }),
                            /*#__PURE__*/ _jsx(Checkbox, {
                                checked: disableEmailCheck,
                                onChange: (event)=>setDisableEmailCheck(event.target.checked),
                                style: {
                                    marginTop: 6
                                },
                                children: __("I confirm this email address is correct.")
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Row, {
                        gutter: 16,
                        children: [
                            /*#__PURE__*/ _jsx(Col, {
                                xs: 24,
                                sm: 12,
                                children: /*#__PURE__*/ _jsx(Form.Item, {
                                    name: "firstName",
                                    label: __("First Name"),
                                    required: true,
                                    rules: [
                                        {
                                            required: true,
                                            message: __("Please enter your first name.")
                                        }
                                    ],
                                    children: /*#__PURE__*/ _jsx(Input, {
                                        placeholder: __("Your First Name"),
                                        autoComplete: "given-name",
                                        ...MATOMO_MASK
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsx(Col, {
                                xs: 24,
                                sm: 12,
                                children: /*#__PURE__*/ _jsx(Form.Item, {
                                    name: "lastName",
                                    label: __("Last Name"),
                                    required: true,
                                    rules: [
                                        {
                                            required: true,
                                            message: __("Please enter your last name.")
                                        }
                                    ],
                                    children: /*#__PURE__*/ _jsx(Input, {
                                        placeholder: __("Your Last Name"),
                                        autoComplete: "family-name",
                                        ...MATOMO_MASK
                                    })
                                })
                            })
                        ]
                    }),
                    customerType === "company" && /*#__PURE__*/ _jsx(Form.Item, {
                        name: "companyName",
                        label: __("Company"),
                        required: true,
                        rules: [
                            {
                                required: true,
                                message: __("Please enter your company name.")
                            }
                        ],
                        children: /*#__PURE__*/ _jsx(Input, {
                            placeholder: __("Your Company"),
                            autoComplete: "organization",
                            ...MATOMO_MASK
                        })
                    }),
                    customerType === "individual" && /*#__PURE__*/ _jsx(Form.Item, {
                        name: WANTS_INVOICE_ADDRESS_FIELD,
                        style: {
                            marginBottom: 16
                        },
                        children: /*#__PURE__*/ _jsx(InvoiceAddressToggle, {})
                    }),
                    /*#__PURE__*/ _jsx(AddressFields, {
                        form: form
                    }),
                    customerType === "company" && /*#__PURE__*/ _jsx(VatIdField, {
                        form: form
                    }),
                    /*#__PURE__*/ _jsx(TeachingsSection, {}),
                    /*#__PURE__*/ _jsx(Form.Item, {
                        style: {
                            marginTop: 24,
                            marginBottom: 0
                        },
                        children: /*#__PURE__*/ _jsx(Button, {
                            type: "primary",
                            size: "large",
                            htmlType: "submit",
                            // Flip to loading on both the immediate click (while
                            // the VAT ID validator is still running) and the
                            // later-resolved `handleSubmit` phase, so the spinner
                            // appears without delay.
                            loading: isBusy || submitRequested,
                            onClick: ()=>setSubmitRequested(true),
                            block: true,
                            className: "checkout-v2-cta-button",
                            children: __("Continue to payment")
                        })
                    })
                ]
            })
        ]
    });
});
/**
 * Switch + label rendered inline so the text sits to the right of the toggle
 * instead of above it as a standard Antd `Form.Item` label would. Uses a
 * `<div>` rather than a `<label>` because a native `<label>` wrapping the
 * Switch re-dispatches the click to the inner control, producing a
 * double-toggle per user click.
 */ const InvoiceAddressToggle = ({ value, onChange, disabled })=>/*#__PURE__*/ _jsxs("div", {
        style: {
            display: "inline-flex",
            alignItems: "center",
            gap: 12
        },
        children: [
            /*#__PURE__*/ _jsx(Switch, {
                checked: !!value,
                onChange: (next)=>onChange?.(next),
                disabled: disabled
            }),
            /*#__PURE__*/ _jsx("span", {
                onClick: ()=>{
                    if (!disabled) {
                        onChange?.(!value);
                    }
                },
                style: {
                    cursor: disabled ? "not-allowed" : "pointer",
                    userSelect: "none"
                },
                children: __("Yes, I want to receive an invoice with my address")
            })
        ]
    });
export { IdentificationForm };
